import { setReadoutData } from '../components/data_readout';
import { Config } from '../config/config';
import { MAX_WMTS_FRAMES } from '../config/constants';
import { calcPrefixFromDate, cloneDate, getDateFromLocalDateStr, dayDifference, getLatestDates, getDateFromUTCDateStr, monthDifference, localDateToNoonUTC } from '../helpers/tools';
import { datasetStore, modalStore, refsStore } from './globalState';
import DATA_READER_DATA from '../data/value_definitions_ar'; // importing the arabic units

class DatasetManager {
	constructor(app) {
		this.app = app;

		/**
		 * The current dataset.
		 * @type {object}
		 * @public
		 */
		this.dataset = null;

		/**
		 * The current dataset group.
		 * @type {object}
		 * @public
		 */
		this.datasetGroup = null;

		/**
		 * Whether the dataset is updated monthly.
		 * @type {boolean}
		 * @public
		 */
		this.isMonthly = false;

		/**
		 * The start and end date limits for the dataset (from the manifest file)
		 * @type {object}
		 * @property {Date} start
		 * @property {Date} end
		 * @public
		 */
		this.manifestLimits = {
			start: null,
			end: null
		};

		/**
		 * List of animation objects
		 * @type {Array<object>}
		 * @public
		 */
		this.animations = [];

		/**
		 * Animation index.
		 * @type {number}
		 * @public
		 */
		this.animationIndex = 0;

		/**
		 * Whether we are animating through datasets.
		 * Currently not an ideal name as it remains true when the animation is paused.
		 * Better would be something like showingRange
		 * @type {boolean}
		 * @public
		 */
		this.hasAnimation = null;

		/**
		 * Whether we are displaying the latest animation dataset
		 * @type {boolean}
		 * @public
		 */
		this.isLatest = null;

		/**
		 * Whether we are displaying the latest seven animation datasets
		 * @type {boolean}
		 * @public
		 */
		this.isLatestSeven = null;

		/**
		 * Whether the animation loop is currently paused.
		 * @type {boolean}
		 * @public
		 */
		this.isPaused = null;

		/**
		 * Dataset's readout information
		 * @type {object}
		 * @public
		 */
		this.readoutData = null;

		/**
		 * Animation start date
		 * @type {Date}
		 * @public
		 */
		this.animationStartDate = null;

		/**
		 * Animation end date
		 * @type {Date}
		 * @public
		 */
		this.animationEndDate = null;

		/**
		 * Dataset group index
		 * @type {number}
		 * @private
		 */
		this._datasetGroupIndex = null;

		/**
		 * Dataset index
		 * @type {number}
		 * @private
		 */
		this._datasetIndex = null;

		/**
		 * Stored, clearable animation interval
		 * @type {number}
		 * @private
		 */
		this._animationInterval = null;

		/**
		 * Interval between animating dataset in milliseconds
		 * @type {number}
		 * @private
		 */
		this._animIntervalTimeMs = 1000;


		/**
		 * Binds
		 */
		this.getDatasetManifestData = this.getDatasetManifestData.bind(this);
		this.calcNumFrames = this.calcNumFrames.bind(this);
		this.setAnimationProps = this.setAnimationProps.bind(this);
		this.setIsLatest = this.setIsLatest.bind(this);
		this.setIsLatestSeven = this.setIsLatestSeven.bind(this);
		this.getAnimationStartDate = this.getAnimationStartDate.bind(this);
		this.getAnimationEndDate = this.getAnimationEndDate.bind(this);
	}

	/**
	 * Sets the start and/or end limits for the current dataset
	 */
	_setManifestLimits() {
		const { startDate, endDate } = this.dataset;
		const start = getDateFromUTCDateStr(startDate);
		const end = getDateFromUTCDateStr(endDate);

		this.manifestLimits.start = localDateToNoonUTC(start);
		this.manifestLimits.end = localDateToNoonUTC(end);
	}

	/**
	 * Sets the current dataset.
	 * This is called every time the url changes.
	 * @param {object} datasetParams
	 * @param {number} datasetParams.altid - the datasetGroup index
	 * @param {number} datasetParams.altView - the dataset index
	 */
	setDataset({ altid, altView }) {
		const { get } = refsStore.stateSnapshot;
		const { vitalData, isSatellitesNow } = get('content');

		// Stop any existing animation.
		this.stopAnimation();

		// Reset dataset.
		this.resetDataset();

		// Satellites current dataset can be set to null.
		if (isSatellitesNow) {
			// Set global dataset state.
			datasetStore.setGlobalState({ currentDataset: null });
			return;
		}

		this._datasetGroupIndex = altid;
		this._datasetIndex = altView;

		// These should have already been checked for validity.
		this.datasetGroup = vitalData.datasetGroups[this._datasetGroupIndex];
		this.dataset = this.datasetGroup.datasets[this._datasetIndex];

		// Set additional variables.
		const { average, readoutData, externalId } = this.dataset;
		this.isMonthly = average === 'month';
		this.readoutData = readoutData;
		this.isVisibleEarth = externalId === 'viirsToday';

		// Update dataset manifest limits.
		this._setManifestLimits();

		// Set global dataset state.
		datasetStore.setGlobalState({ currentDataset: this.dataset });
	}

	/**
	 * Sets the hasAnimation local, and global state boolean.
	 * @param {boolean} hasAnimation
	 */
	setHasAnimation(hasAnimation) {
		// Set local hasAnimation
		this.hasAnimation = hasAnimation;

		// Set global hasAnimation and, if true, isLoading
		datasetStore.setGlobalState({
			hasAnimation,
			...hasAnimation && { isLoading: true }
		});
	}

	/**
	 * Sets the isPaused local state boolean.
	 * @param {boolean} animating
	 */
	setPaused(paused) {
		this.isPaused = paused;

		datasetStore.setGlobalState({ isPaused: this.isPaused });
	}

	/**
	 * Sets the isLatest local state boolean.
	 * @param {boolean} isLatest
	 */
	setIsLatest(isLatest) {
		this.isLatest = isLatest;
	}

	/**
	 * Sets the isLatestSeven local state boolean.
	 * @param {boolean} isLatestSeven
	 */
	setIsLatestSeven(isLatestSeven) {
		this.isLatestSeven = isLatestSeven;
	}

	/**
	 * Sets local booleans, depending on URL query input
	 * @param {object} params
	 * @param {string} params.start YYYY-MM-DD format
	 * @param {string} params.end YYYY-MM-DD format
	 * @param {string} params.animating
	 */
	setDatasetBools({ start, end, animating } = {}) {
		// Set hasAnimation
		const hasAnimation = animating === 't';
		this.setHasAnimation(hasAnimation);

		// Set isLatest and isLatestSeven.
		const noPassedDates = !start && !end;

		const isLatest = !hasAnimation && noPassedDates;
		const isLatestSeven = hasAnimation && noPassedDates;

		this.setIsLatest(isLatest);
		this.setIsLatestSeven(isLatestSeven);
	}

	/**
	 * Determines the start and end dates, as well as the this.animation array.
	 * @param {object} params
	 * @param {string} params.startDateStr YYYY-MM-DD format
	 * @param {string} params.endDateStr YYYY-MM-DD format
	 * @param {Array<object>} params.animations options animations array
	 */
	setAnimationProps({ startDateStr, endDateStr, animations } = {}) {
		/**
		 * Determine whether to set start and end animation dates if passed.
		 * Either if passed from the query, or wehave a passed animations array (from VE manager)
		 */
		const setDates = (startDateStr && endDateStr) || animations?.length;

		if (setDates) {
			this.animationStartDate = animations ? getDateFromUTCDateStr(animations[0].date) : getDateFromLocalDateStr(startDateStr);
			this.animationEndDate = animations ? getDateFromUTCDateStr(animations[animations.length - 1].date) : getDateFromLocalDateStr(endDateStr);
		}

		// Create animation array from available datasets.
		this.animations = animations ?? this.createAnimationArray() ?? [];

		// Set global store animation start and end dates.
		datasetStore.setGlobalState({
			animationDates: {
				start: this.getAnimationStartDate(),
				end: this.getAnimationEndDate()
			}
		});
	}

	/**
	 * Returns this.animationStartDate if it's not null, otherwise checks the animation array
	 * and returns the date of the first item.
	 */
	getAnimationStartDate() {
		return (this.animationStartDate && cloneDate(this.animationStartDate))
		|| (this.animations[0]?.date && getDateFromUTCDateStr(this.animations[0].date));
	}

	/**
	 * Returns this.animationEndDate if it's not null, otherwise checks the animation array
	 * and returns the date of the last item.
	 */
	getAnimationEndDate() {
		return (this.animationEndDate && cloneDate(this.animationEndDate))
		|| (this.animations[this.animations.length - 1]?.date && getDateFromUTCDateStr(this.animations[this.animations.length - 1].date));
	}

	/**
	 * Sets local and global animationIndex.
	 * @param {number} index
	 */
	setAnimationIndex(index) {
		this.animationIndex = index;
		datasetStore.setGlobalState({ animationIndex: this.animationIndex });
	}

	/**
	 * Resets all dataset props to null.
	 */
	resetDataset() {
		// Remove data readout when changing vital signs
		setReadoutData(null);

		// Reset variables.
		this._datasetGroupIndex = null;
		this._datasetIndex = null;
		this.hasAnimation = null;

		this.datasetGroup = null;
		this.dataset = null;

		this.isMonthly = null;
		this.readoutData = null;
		this.isVisibleEarth = null;

		this.animationStartDate = null;
		this.animationEndDate = null;

		this.manifestLimits = {
			start: null,
			end: null
		};
	}

	/**
	 * Constructs the latest tileset path
	 * @param {number} index
	 * @returns
	 */
	getTilesetPath(index = -1) {
		if (index !== -1 && this.animations[index]) {
			return `${Config.dynamicAssetsUrl}/earth/data/${this.animations[index].tilePathPrefix}`;
		}
		if (this.dataset?.latestTileset) {
			return `${Config.dynamicAssetsUrl}/earth/data/${this.dataset.latestTileset.tilePathPrefix}`;
		}
		return null;
	}

	/**
	 * Wait for enabling of WMTS if we're using it.
	 * Display missing date warnings if necessary.
	 * Starting (or ending) animation.
	 * Setting to latest index if no animation.
	 */
	async initAnimation() {
		const { get } = refsStore.stateSnapshot;
		const { isSatellitesNow } = get('content');

		/**
		 * If we have animation, we need to:
		 * - enable WMTS if on visible earth
		 * - set the first animation index so the user has something to look at
		 * - check whether we have any missing frames and show the modal if so
		 * - start the animation
		 */
		if (this.hasAnimation && !isSatellitesNow) {
			// Load all animations

			// Wait for WMTS tiles to load.
			if (this.isVisibleEarth) {
				const loadingTilesPromises = await get('visibleEarth').enableWMTS();
				loadingTilesPromises.length && await Promise.all(loadingTilesPromises);
			}

			// If we have animations, set the first index.
			this.animations.length && this.changeDatasetByIndex(0);

			// Show modal if we have missing datasets.
			const numFrames = this.calcNumFrames();
			const startDate = this.getAnimationStartDate();
			const endDate = this.getAnimationEndDate();

			/**
			 * There are two ways to determine whether we have missing datasets.
			 *
			 * 1) If we're on visible earth and the number of frames is within the MAX_WMTS_FRAMES (currently 7),
			 * it's possible that the number of day between the first and the last animation dates ends up
			 * being wider than 7 frames, ie. some frames are blank/missing.
			 *
			 * 2) If we are on cubemap datasets, the manifest data will have determined whether we have missing dates,
			 * in which case the number of animations will simply be less than the requested number of frames.
			 */
			let numMissingDatasets = 0;

			// Unique case for visible earth's wmts checks.
			if (this.isVisibleEarth && numFrames <= MAX_WMTS_FRAMES) {
				const frameDiff = dayDifference(startDate, endDate) + 1;
				if (frameDiff > numFrames) {
					numMissingDatasets = frameDiff - numFrames;
				}
			} else if (this.animations.length < numFrames) {
				numMissingDatasets = numFrames - this.animations.length;
			}

			if (numMissingDatasets > 0) {
				modalStore.setGlobalState({
					isVisible: true,
					startRange: startDate,
					endRange: endDate,
					numMissingDatasets,
					onClose: () => {
						this.startAnimation();
					}
				});
			} else if (this.animations.length === 0) {
				// There are no datasets available.
				modalStore.setGlobalState({
					isVisible: true,
					startRange: startDate,
					endRange: endDate,
					numMissingDatasets: null,
					onClose: () => {
						get('route').navigate({ __remove: ['start', 'end', 'animating'] });
					}
				});
			} else {
				this.startAnimation();
			}
		} else {
			// If we dont have animation, we can reset any previous ones and set the frame to the latest.
			this.resetAnimation();
			this.setToLatestIndex();
		}
	}

	/**
	 * Calculates the number of dataset frames depending on animtion start and end dates
	 * @returns {number}
	 */
	calcNumFrames() {
		// If no animation we only want 1 frame.
		if (!this.hasAnimation) {
			return 1;
		}
		// If latest 7, return 7.
		if (this.isLatestSeven) {
			return 7;
		}

		const startDate = this.getAnimationStartDate();
		const endDate = this.getAnimationEndDate();

		// Otherwise calculate the frame difference between start and end dates. Add 1 to include the dates themselves.
		return 1 + (this.isMonthly ?
			monthDifference(startDate, endDate) :
			dayDifference(startDate, endDate));
	}

	/**
	 * Determines the this.animations array.
	 * Is it calculated by inputted start and end dates?
	 * Is it calculated from the last 7 available datasets?
	 * What happens if we are missing datasets and how do we tell the user of such horrors?
	 * @returns {Array}
	 */
	createAnimationArray() {
		// We wont have any animations for satellitesNow.
		const { get } = refsStore.stateSnapshot;
		const { isSatellitesNow } = get('content');

		if (isSatellitesNow) {
			return [];
		}

		const { externalId } = this.dataset;


		const { frequency, startDate: manifestStart, endDate: manifestEnd, missingDates } = get('content').datasetsManifest[externalId];

		const numFrames = this.calcNumFrames();

		/**
		 * Between animation and manifest dates, we need to determine:
		 * - the latest start date
		 * - the earliest end date
		 */
		let latestStartDate = getDateFromUTCDateStr(manifestStart);
		let earliestEndDate = getDateFromUTCDateStr(manifestEnd);

		if (this.animationStartDate && localDateToNoonUTC(latestStartDate).valueOf() < localDateToNoonUTC(this.animationStartDate).valueOf()) {
			latestStartDate = this.animationStartDate;
		}
		if (this.animationEndDate && localDateToNoonUTC(earliestEndDate).valueOf() > localDateToNoonUTC(this.animationEndDate).valueOf()) {
			earliestEndDate = this.animationEndDate;
		}

		const latestDates = getLatestDates({
			frequency,
			startDate: latestStartDate,
			endDate: earliestEndDate,
			missingDates
		}, numFrames);

		const result = latestDates.map(currDate => ({
			date: currDate.toISOString(),
			dateObject: currDate,
			tilePathPrefix: `${externalId}/${calcPrefixFromDate(currDate)}`
		}));

		/**
		 * At this point we may have to set isLatest and  isLatestSeven again.
		 * This is because there are 2 ways to have the latest:
		 * - No dates are entered
		 * - Correct date range is entered that may also be the latest seven!
		 *
		 * Now is the point to check, dependent on result length and earliest end date matching the manifest end date.
		 * The VE manager handles this for wmts.
		 */
		const endDateMatchesManifest = localDateToNoonUTC(earliestEndDate).valueOf() === localDateToNoonUTC(getDateFromUTCDateStr(manifestEnd)).valueOf();

		const isLatest = endDateMatchesManifest && !this.hasAnimation && result.length === 1;
		const isLatestSeven = endDateMatchesManifest && this.hasAnimation && result.length === 7;

		this.setIsLatest(isLatest);
		this.setIsLatestSeven(isLatestSeven);

		return result;
	}

	setToLatestIndex() {
		const { get } = refsStore.stateSnapshot;
		const { isSatellitesNow } = get('content');

		if (isSatellitesNow) {
			return;
		}

		this.changeDatasetByIndex(this.animations.length - 1);
	}

	prevDataset(pauseAnimation = true) {
		// Pause animation if necessary.
		pauseAnimation && this.setPaused(true);

		const oldIndex = this.animationIndex;
		const newIndex = oldIndex > 0 ? oldIndex - 1 : this.animations.length - 1;
		this.changeDatasetByIndex(newIndex);
	}

	nextDataset(pauseAnimation = true) {
		// Pause animation if necessary.
		pauseAnimation && this.setPaused(true);

		const oldIndex = this.animationIndex;
		const newIndex = oldIndex < this.animations.length - 1 ? oldIndex + 1 : 0;
		this.changeDatasetByIndex(newIndex);
	}

	changeDatasetByIndex(index) {
		const { get } = refsStore.stateSnapshot;
		this.setAnimationIndex(index);

		if (this.isVisibleEarth) {
			get('visibleEarth').changeIndex(index);
		} else {
			if (index > this.animations.length) {
				this.setAnimationIndex(this.animations.length - 1);
				index = this.animations.length - 1;
			}
			let file = '';
			if (this.animations[index]) {
				file = this.getTilesetPath(index);
			} else {
				file = this.getTilesetPath();
			}

			const earthEntity = this.app.scene.get('earth');

			earthEntity.getComponentByType('atmosphere')?.setEnabled(false);
			earthEntity.getComponentByType('spheroidLOD')?.setEnabled(true);
			earthEntity.get('spheroidLOD').setTexture('color', file + '$FACE.png');
			get('scene').loadCanvas(file);
		}
	}

	// Makes sure global isLoading and paused are set to false, resets the animation back to its first frame.
	resetAnimation() {
		// Set global isLoading to false.
		datasetStore.setGlobalState({ isLoading: false });

		this.setPaused(false);
		this.setAnimationIndex(0);
	}

	/**
	 * Starts the animation interval. Only called by initAnimation
	 * @returns
	 */
	async startAnimation() {
		// Stop animation.
		this.stopAnimation();

		// Reset the animation.
		this.resetAnimation();

		// Allow fairly arbitrary pause to load the first texture into the actual graphics card.
		// const pauseTimeMs = 10000;
		// await new Promise(resolve => setTimeout(resolve, pauseTimeMs));

		// Start animation interval.
		this._animationInterval = setInterval(() => {
			if (this.isPaused) {
				return;
			}

			const pauseAnimation = false;
			this.nextDataset(pauseAnimation);
		}, this._animIntervalTimeMs);
	}

	/**
	 * Simply clears the animation interval.
	 */
	stopAnimation() {
		clearInterval(this._animationInterval);
	}


	/**
	 * Sets the url query to trigger playing of the latest seven datasets for current vital sign.
	 */
	playLatestSeven() {
		const { get } = refsStore.stateSnapshot;

		// Navigate to default animation.
		const navigated = get('route').navigate({
			animating: 't',
			start: '',
			end: ''
		});

		// In case of no change, router won't navigate
		// Force play
		if (!navigated) {
			this.initAnimation();
		}
	}

	/**
	 * Sets the url query to stop animation and trigger the latest dataset.
	 */
	resetToLatest() {
		const { get } = refsStore.stateSnapshot;
		get('route').navigate({
			animating: 'f',
			start: '',
			end: ''
		});
	}

	getCylThumbSrc(dataset = this.dataset) {
		const { cylThumbFile } = dataset?.latestTileset || {};

		return cylThumbFile && `${Config.dynamicAssetsUrl}/earth/data/${cylThumbFile}`;
	}

	/**
	 * Returns current dataset's manifest data.
	 * @returns {object}
	 */
	getDatasetManifestData() {
		const { get } = refsStore.stateSnapshot;
		const { externalId } = this.dataset || {};
		return externalId && get('content')?.datasetsManifest?.[externalId];
	}
}

export default DatasetManager;
